/**
 * Confirms dropping a jurisdiction from a report that has already started.
 *
 * The jurisdiction leaves the report's subjects and the matching tag leaves its
 * Backoffice project, so ops reporting stops counting it. It asks for a reason,
 * which is recorded with who and when. The server refuses it while a partner
 * assignment still covers that jurisdiction; the dialog says so up front.
 */

import { useEffect, useState } from 'react';

import { Button, Group, List, Modal, Stack, Text, Textarea } from '@mantine/core';

import { isValidReason, MIN_REASON_LENGTH } from '@/types/commercial-change';

interface RemoveJurisdictionDialogProps {
  opened: boolean;
  /** The jurisdiction being removed, for the heading. */
  jurisdictionName: string;
  submitting?: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}

export function RemoveJurisdictionDialog({
  opened,
  jurisdictionName,
  submitting = false,
  onConfirm,
  onCancel,
}: Readonly<RemoveJurisdictionDialogProps>) {
  const [reason, setReason] = useState('');

  // Each time the dialog opens it starts blank — a reason is for one removal.
  useEffect(() => {
    if (opened) {
      setReason('');
    }
  }, [opened]);

  const valid = isValidReason(reason);
  const typed = reason.trim().length;

  return (
    <Modal
      opened={opened}
      onClose={submitting ? () => undefined : onCancel}
      title={`Remove ${jurisdictionName}?`}
      data-testid="remove-jurisdiction-dialog"
    >
      <Stack gap="sm">
        <List size="sm" spacing={4}>
          <List.Item>
            {jurisdictionName} is removed from this report&apos;s subjects and from its Backoffice
            project.
          </List.Item>
          <List.Item>
            It can&apos;t be removed while a partner assignment still covers it — cancel that
            assignment first.
          </List.Item>
        </List>

        <Textarea
          label="Reason"
          placeholder="e.g. Client narrowed the scope to the UK"
          minRows={3}
          autosize
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          description={
            valid
              ? undefined
              : `At least ${MIN_REASON_LENGTH} characters (${typed}/${MIN_REASON_LENGTH})`
          }
          data-testid="remove-jurisdiction-reason"
          data-autofocus
        />
        <Text size="xs" c="dimmed">
          Recorded with your name and the time.
        </Text>

        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel} disabled={submitting}>
            Keep it
          </Button>
          <Button
            color="red"
            onClick={() => void onConfirm(reason.trim())}
            disabled={!valid}
            loading={submitting}
            data-testid="remove-jurisdiction-confirm"
          >
            Remove jurisdiction
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
